import { ReactNode, useState } from 'react'
import { com } from '@eidu/web'
import { Button, Checkbox, Group, Popover, Stack, Text } from '@common/ui'
import DraftFieldValue from '../../domain/entity/DraftFieldValue'
import EntityFormInput from '../input/EntityFormInput'
import useEntityById from '../../io/useEntityById'
import Field = com.eidu.entity.field.Field
import FieldType = com.eidu.entity.field.FieldType
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import entityIdFromStringOrNull = com.eidu.entity.entityIdFromStringOrNull

const NULL_FILTER_VALUE = 'null'

export type FilterPopoverProps = {
  field: Field
  currentValue: string | null
  onApply: (value: string | null, label?: string) => void
  onClear: () => void
  opened: boolean
  onClose: () => void
  target: ReactNode
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

type FilterFormProps = Omit<FilterPopoverProps, 'opened' | 'target'>

const FilterForm = ({ field, currentValue, onApply, onClear, onClose, entityTypes }: FilterFormProps) => {
  const initialValue = currentValue === NULL_FILTER_VALUE ? '' : currentValue ?? ''
  const [fieldValue, setFieldValue] = useState<DraftFieldValue>({
    fieldId: field.id,
    type: field.type,
    value: initialValue,
  })
  const [isNull, setIsNull] = useState(currentValue === NULL_FILTER_VALUE)

  const selectedEntityId =
    field.type === FieldType.Reference ? entityIdFromStringOrNull(fieldValue.value) ?? undefined : undefined
  const { entity: selectedEntity } = useEntityById(selectedEntityId)

  const handleApply = () => {
    if (isNull) {
      onApply(NULL_FILTER_VALUE)
    } else if (fieldValue.value.trim()) {
      const label = selectedEntity?.primaryLabelText
      onApply(fieldValue.value.trim(), label)
    } else {
      onClear()
    }
    onClose()
  }

  const handleClear = () => {
    onClear()
    onClose()
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Filter: {field.name}
      </Text>

      <EntityFormInput
        field={field}
        fieldValue={fieldValue}
        entityTypes={entityTypes}
        updateField={setFieldValue}
        disabled={isNull}
        dropdownWithinPortal={false}
      />

      <Checkbox
        checked={isNull}
        onChange={(e) => {
          setIsNull(e.currentTarget.checked)
          if (e.currentTarget.checked) {
            setFieldValue({ ...fieldValue, value: '' })
          }
        }}
        size="sm"
        label="Empty"
      />

      <Group gap="xs" justify="flex-end">
        <Button size="xs" variant="subtle" onClick={handleClear}>
          Clear
        </Button>
        <Button size="xs" onClick={handleApply}>
          Apply
        </Button>
      </Group>
    </Stack>
  )
}

const FilterPopover = ({ opened, target, ...formProps }: FilterPopoverProps) => (
  <Popover
    opened={opened}
    onChange={(isOpened) => {
      if (!isOpened) formProps.onClose()
    }}
    position="bottom-start"
    shadow="md"
    trapFocus
  >
    <Popover.Target>{target}</Popover.Target>
    <Popover.Dropdown data-testid="filter-popover" p="md" miw={280} maw={400}>
      <FilterForm {...formProps} />
    </Popover.Dropdown>
  </Popover>
)

export { NULL_FILTER_VALUE }
export default FilterPopover
